import { KnowledgeBase } from '@/interfaces/IKnowledgeBase';
import { computeEstimatedTokens, getColorAgainstConsumedTokens } from '@/utils/helper';
import { DeleteFilled, SyncOutlined } from '@ant-design/icons';
import { InfoOutlined } from '@mui/icons-material';
import { Button, Card, notification, Switch, Tag, Tooltip, Typography } from 'antd';
import Paragraph from 'antd/es/typography/Paragraph';
import DOMPurify from 'dompurify';
import { KNOWLEDGE_BASE_ENTRY_STATUS } from 'src/enums/EKnowledgeBase';
import { KNOWLEDGE_BASE_ENTRY_TYPE } from 'src/enums/EKnowledgeBase';

const { Text } = Typography;

interface KnowledgeBaseCardProps {
	item: KnowledgeBase;
	index: number;
	dataNumber: string;
	selectedAgentId: string;
	refetch: () => void;
	openViewModal: (item: KnowledgeBase, index: number) => void;
	openEditModal: (index: number, content: string, itemType?: string, syncType?: string) => void;
	updateActivationMutation: any;
	manualSyncMutation: any;
	deleteMutation: any;
	setLoading: (loading: boolean) => void;
	isLoading: boolean;
	openDeleteModal: (index: number) => void;
	knowledgeBase?: KnowledgeBase[];
}

const getColor = (type: string) => {
	switch (type) {
		case 'text':
			return {
				base: 'blue',
				border: 'border-blue-300',
				hoverBorder: 'hover:border-blue-400',
			};
		case 'link':
			return {
				base: 'green',
				border: 'border-green-300',
				hoverBorder: 'hover:border-green-400',
			};
		case 'file':
			return {
				base: 'purple',
				border: 'border-purple-300',
				hoverBorder: 'hover:border-purple-400',
			};
		default:
			return {
				base: 'red',
				border: 'border-red-300',
				hoverBorder: 'hover:border-red-400',
				hoverBg: 'hover:bg-red-50',
			};
	}
};

export const KnowledgeBaseCard: React.FC<KnowledgeBaseCardProps> = ({
	item,
	index,
	dataNumber,
	selectedAgentId,
	refetch,
	openViewModal,
	openEditModal,
	updateActivationMutation,
	manualSyncMutation,
	setLoading,
	openDeleteModal,
}) => {
	const consumedTokens = item.consumedTokens ?? computeEstimatedTokens(item.content);

	const color = getColor(item.type);
	const statusColor = item.isActive ? 'text-blue-700 bg-blue-100' : 'text-red-700 bg-red-100';

	const handleToggleActivation = (checked: boolean) => {
		setLoading(true);

		updateActivationMutation.mutate(
			{
				number: dataNumber,
				entryIndex: index,
				isActive: checked,
				agentId: selectedAgentId,
			},
			{
				onSettled: () => setLoading(false),
				onSuccess: () => refetch(),
				onError: (error: any) => {
					notification.error({
						message: 'Failed to update status',
						description: error?.message || 'An error occurred.',
					});
				},
			},
		);
	};

	const handleManualSync = () => {
		setLoading(true);

		manualSyncMutation.mutate(
			{
				number: dataNumber,
				entryIndex: index,
				entry: JSON.stringify(item),
				agentId: selectedAgentId,
			},
			{
				onSettled: () => setLoading(false),
				onError: (error: any) => {
					notification.error({
						message: 'Failed to sync',
						description: error?.message || 'An unknown error occurred during sync.',
					});
				},
			},
		);
	};

	const displayContent = item.content
		? DOMPurify.sanitize(item.content)
				.replace(/<[^>]*>?/gm, '')
				.substring(0, 200) + (item.content.length > 200 ? '...' : '')
		: '';

	const handleViewOrEdit = (type: string, status: string) => {
		if (status === 'processing') {
			return;
		}

		if (type === 'text') {
			openEditModal(index, item.content, item.type);
		} else {
			openViewModal(item, index);
		}
	};

	return (
		<div
			className="relative flex flex-col w-full"
			onClick={() => {
				handleViewOrEdit(item.type, item.status);
			}}
		>
			<Card
				hoverable
				className={`rounded-3xl h-full shadow-lg hover:shadow-xl transition-all duration-300 border-2 ${color.border} ${color.hoverBorder} hover:scale-105 hover:-translate-y-1 w-full max-w-xs md:max-w-sm lg:max-w-md xl:max-w-none`}
				style={{
					display: 'flex',
					flexDirection: 'column',
					justifyContent: 'space-between',
				}}
				styles={{
					body: {
						display: 'flex',
						flexDirection: 'column',
						padding: '1.25rem',
						height: '100%',
					},
				}}
			>
				<div>
					<div className="flex justify-between mb-1 items-baseline">
						<Tag
							className={`capitalize px-2 py-1 border-0 rounded-full font-medium text-sm bg-opacity-40 text-${color.base}-700 bg-${color.base}-100`}
						>
							{item.type}
						</Tag>
						<div className="flex items-center">
							<Switch
								checked={item.isActive}
								onChange={(checked, e) => {
									e.stopPropagation();
									handleToggleActivation(checked);
								}}
								className={`transition ${item.isActive ? '!bg-blue-600' : '!bg-red-300'}`}
								size="small"
								disabled={
									item.status === KNOWLEDGE_BASE_ENTRY_STATUS.PROCESSING || !!item.structuredData
								}
							/>
							<Tag
								className={`border-0 rounded-full font-medium px-2 py-0.5 text-sm bg-opacity-40 ${statusColor}`}
							>
								{item.isActive ? 'Active' : 'Inactive'}
							</Tag>
							<Button
								type="text"
								icon={<DeleteFilled />}
								onClick={(event) => {
									event.stopPropagation();
									openDeleteModal(index);
								}}
								disabled={
									item.status === KNOWLEDGE_BASE_ENTRY_STATUS.PROCESSING || !!item.structuredData
								}
								className="mx-0 text-red-600 hover:text-red-700 hover:bg-red-50"
								size="small"
								title={
									item.structuredData
										? 'You cannot delete this entry as it contains important business data'
										: 'Delete'
								}
							/>
						</div>
					</div>

					{consumedTokens > 0 && (
						<div className="flex justify-end mb-2">
							<Tag color={getColorAgainstConsumedTokens(consumedTokens)}>
								Tokens consumed: {consumedTokens}
							</Tag>
						</div>
					)}

					<Text strong className="block mb-1 text-gray-800 text-sm">
						Content:
					</Text>
					<div className="relative min-h-[4rem] mb-1">
						<Paragraph className="text-gray-700 text-sm overflow-hidden line-clamp-6">
							{item.status === KNOWLEDGE_BASE_ENTRY_STATUS.COMPLETED ? (
								displayContent
							) : item.status === KNOWLEDGE_BASE_ENTRY_STATUS.FAILED ? (
								<div className="flex items-center gap-1">
									<Tag className="bg-red-50 text-red-700 border-0 font-medium px-2 py-0.5">
										Failed
									</Tag>
									<Tooltip title="The AI failed to generate relevant data from your given resource. Please sync again or try deleting it and adding it again manually.">
										<InfoOutlined className="text-gray-400 !w-4 !h-4 cursor-help" />
									</Tooltip>
								</div>
							) : (
								<div className="flex items-center gap-1">
									<Tag className="bg-amber-50 text-amber-700 border-0 font-medium px-2 py-0.5">
										Processing
									</Tag>
									<Tooltip title="The AI is working on generating relevant data from your given resource. This can take 3-5 minutes depending on the amount of data. You'll be able to see it here once complete. Use the refresh button to check the status.">
										<InfoOutlined className="text-gray-400 !w-4 !h-4 cursor-help" />
									</Tooltip>
								</div>
							)}
						</Paragraph>
					</div>
				</div>
				<div className="flex flex-wrap lg:flex-row items-center justify-center py-1 max-w-full">
					{item.type === KNOWLEDGE_BASE_ENTRY_TYPE.LINK && item.syncType && (
						<Tag className="bg-green-50 text-green-700 border-0 font-small px-1 py-1 rounded-3xl">
							Auto Sync:{' '}
							<strong>{item.syncType.charAt(0).toUpperCase() + item.syncType.slice(1)}</strong>
						</Tag>
					)}
					{item.type === 'link' && item.content && (
						<Button
							type="default"
							icon={<SyncOutlined />}
							onClick={(event) => {
								event.stopPropagation();
								handleManualSync();
							}}
							className="border-blue-100 bg-blue-50 hover:bg-blue-100 hover:border-blue-200 text-blue-700 h-xl text-xs"
							size="small"
							disabled={item.status === KNOWLEDGE_BASE_ENTRY_STATUS.PROCESSING}
						>
							Sync Now
						</Button>
					)}
				</div>
			</Card>
		</div>
	);
};
